import { allColors, FoodsBadge, FoodsIcon, IconMapping, NumberWithUnit } from "@foodpilot/foods";
import { Stack, Typography, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import { ValidatedState, ValidatedTag } from "../ValidatedTag";
import { CircularProgress } from "src/components/CircularProgress";
import SpecificityRateDisplay from "src/components/Subscore/SpecificityRate/SpecificityRateDisplay.tsx";
import { NumericalScoreValue } from "src/api/client-api/olapScores/olapScoresSchema";

type SummaryBlockProps = {
    title: string;
    type: "questionnaire" | "action_plan" | "target";
    icon: keyof IconMapping;
    score: number | null | undefined;
    subScore?: number | null | undefined;
    unit: string;
    tag?: ValidatedState;
    completionPercent: number | undefined;
    completion: string | undefined;
    completionInfo: string;
    datavizColorIndex: keyof typeof allColors;
    isSelected: boolean;
    specificityRate?: NumericalScoreValue | undefined;
    hideChevron?: boolean;
};
export const SummaryBlock = (props: SummaryBlockProps) => {
    const {
        type,
        title,
        datavizColorIndex,
        score,
        subScore,
        unit,
        completion,
        completionInfo,
        isSelected,
        icon,
        specificityRate,
        hideChevron = false,
    } = props;
    const { t } = useTranslation();
    const theme = useTheme();

    const datavizColor = theme.custom.dataviz.colorsWithExtra[datavizColorIndex];

    const subScoreAsUnit = `/ ${subScore}`;
    const hoveredBorderClass = isSelected ? `2px solid ${datavizColor.variant[700]}` : "1px solid transparent";
    const borderClass = `1px solid ${datavizColor.variant[700]}`;

    return (
        <Stack
            sx={{
                gap: theme.spacing(2),
                justifyContent: "space-between",
                height: "100%",
                border: hoveredBorderClass,
                backgroundColor: isSelected ? theme.palette.grey[200] : "transparent",
                borderRadius: "8px",
                padding: `calc(${theme.spacing(2)} + ${isSelected ? "0px" : "1px"})`,
                transition: "border-color ease-in-out 0.2s, color ease-in-out 0.3s",
                "&:hover":
                    !isSelected ?
                        {
                            border: borderClass,
                            backgroundColor: theme.palette.grey[200],
                            transition: "border-color ease-in-out 0.2s, color ease-in-out 0.3s",
                        }
                    :   {},
            }}
        >
            <Stack
                flexDirection={"row"}
                flex={1}
                gap={theme.spacing(2)}
                justifyContent={"space-between"}
                alignItems={"flex-start"}
            >
                <Stack flexDirection="row" gap={theme.spacing(2)}>
                    <FoodsBadge
                        size={5}
                        icon={icon}
                        boxProps={{
                            sx: {
                                padding: theme.spacing(1),
                                color: datavizColor.variant[700],
                                backgroundColor: datavizColor.variant[100],
                                borderRadius: "8px",
                            },
                        }}
                    />
                    <Typography variant="h4" alignSelf={"center"}>
                        {title}
                    </Typography>
                </Stack>
                <ValidatedTag state={props.tag} />
            </Stack>
            <Stack flexDirection="row" alignItems={"center"} minHeight="50px">
                {type === "target" && (score == null || score === 0) ?
                    <Typography
                        sx={{
                            color: "#767673",
                        }}
                    >
                        {t("common.coming_soon", "Coming Soon")}
                    </Typography>
                :   <NumberWithUnit value={score ?? null} unit={subScore ? subScoreAsUnit : unit} size={"big"} />}
            </Stack>
            {specificityRate !== undefined && (
                <Stack flexDirection="row" flex={1} alignItems={"center"}>
                    <SpecificityRateDisplay specificityRate={specificityRate} />
                </Stack>
            )}
            <Stack
                flexDirection="row"
                alignItems={"center"}
                sx={{
                    width: "100%",
                    height: "100%",
                    backgroundColor: datavizColor.variant[100],
                    borderRadius: "8px",
                    maxHeight: "70px",
                }}
            >
                <Stack
                    display="flex"
                    flexDirection="row"
                    padding={theme.spacing(1)}
                    gap={theme.spacing(1.5)}
                    alignItems={"center"}
                    minHeight="70px"
                >
                    {type === "target" && (score == null || score === 0) ?
                        <Stack flexDirection="row" gap={theme.spacing(1.5)} alignItems={"center"}>
                            <FoodsBadge
                                size={3.5}
                                icon="informations"
                                boxProps={{
                                    sx: {
                                        color: datavizColor.variant[700],
                                        backgroundColor: datavizColor.variant[100],
                                        borderRadius: "8px",
                                    },
                                }}
                            />
                            <Typography
                                sx={{
                                    fontWeight: 700,
                                    fontSize: "14px",
                                    lineHeight: "1.5em",
                                    color: "#393A36",
                                }}
                            >
                                {t("understand_my_sectoral_target", "Understand my sectoral target")}
                            </Typography>
                        </Stack>
                    : completion !== undefined ?
                        <>
                            <CircularProgress progress={props.completionPercent ?? 0} color={datavizColor.main} />
                            <Stack sx={{ ml: theme.spacing(8) }}>
                                <Typography variant="h3">{completion}</Typography>
                                <Typography variant="body">{completionInfo}</Typography>
                            </Stack>
                        </>
                    :   null}
                </Stack>
            </Stack>
            {!hideChevron && (
                <Stack
                    sx={{
                        alignSelf: "center",
                        marginTop: "auto",
                    }}
                >
                    <FoodsIcon size={2} icon={isSelected ? "arrowUpShort" : "arrowDownShort"} />
                </Stack>
            )}
        </Stack>
    );
};
